import { HttpErrorResponse } from "@angular/common/http";

export interface PlatformConfigurationDiagnosticScopeQuery {
  configurationCode: string;
  scopeTypeCode?: string;
  scopeKey?: string;
  asOfUtc?: string | Date;
}

export interface PlatformConfigurationLookupCatalogRequest extends PlatformConfigurationDiagnosticScopeQuery {
  domainCode: string;
}

export interface PlatformConfigurationFieldMetadataRequest extends PlatformConfigurationDiagnosticScopeQuery {
  entityCode: string;
}

export interface PlatformConfigurationRuleBindingsRequest extends PlatformConfigurationDiagnosticScopeQuery {
  subjectCode?: string;
  bindingTypeCode?: string;
}

export interface PlatformConfigurationTerminologyHealthRequest extends PlatformConfigurationDiagnosticScopeQuery {
  localeCode: string;
  channelCode?: string;
  audienceCode?: string;
  tenantId?: string;
}

export interface PlatformConfigurationDiagnosticResult<T> {
  state: PlatformConfigurationDiagnosticState;
  data: T | null;
  error: PlatformConfigurationDiagnosticDisplayError | null;
}

export type PlatformConfigurationDiagnosticState =
  | "resolved"
  | "invalid"
  | "missing"
  | "conflict"
  | "unavailable";

export interface PlatformConfigurationDiagnosticDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
  reasonCode?: string;
  matchedBindingCount?: number;
  correlationId?: string | null;
}

export interface PlatformConfigurationProblemDetails {
  title?: string;
  detail?: string;
  status?: number;
  reasonCode?: string;
  matchedBindingCount?: number;
  errors?: Record<string, string[] | string>;
}

export interface EzResolvedConfigurationVersion {
  configurationVersionId: number;
  configurationCode: string;
  versionCode: string;
  displayName?: string | null;
  scopeTypeCode?: string | null;
  scopeKey?: string | null;
  matchedDimensions?: Record<string, string | null>;
}

export interface EzConfigurationResolutionTraceEntry {
  scopeTypeCode?: string | null;
  scopeKey?: string | null;
  candidateBindingCount?: number;
  matchedBindingCount?: number;
}

export interface EzConfigurationResolutionResult {
  disposition: string;
  reasonCode?: string | null;
  matchedBindingCount: number;
  version?: EzResolvedConfigurationVersion | null;
  trace?: EzConfigurationResolutionTraceEntry[];
}

export interface EzConfigurationCatalogEnvelope {
  configuration: EzResolvedConfigurationVersion;
}

export interface EzLookupCatalog extends EzConfigurationCatalogEnvelope {
  domainCode: string;
  values: EzLookupCatalogValue[];
}

export interface EzLookupCatalogValue {
  valueCode: string;
  displayText: string;
  sortOrder: number;
  isEnabled: boolean;
  isDefault?: boolean;
  appliedScopeTypeCode?: string | null;
  appliedScopeKey?: string | null;
}

export interface EzFieldMetadataCatalog extends EzConfigurationCatalogEnvelope {
  entityCode: string;
  fields: EzFieldMetadataCatalogField[];
}

export interface EzFieldMetadataCatalogField {
  fieldCode: string;
  requiredModeCode: string;
  visibleModeCode: string;
  editableModeCode: string;
  ruleCode?: string | null;
  clientHintCode?: string | null;
}

export interface EzIssueCatalog extends EzConfigurationCatalogEnvelope {
  entries: EzIssueCatalogEntry[];
}

export interface EzIssueCatalogEntry {
  issueCode: string;
  ruleCode?: string | null;
  categoryCode: string;
  severityCode: string;
  clientHandlingHintCode?: string | null;
  defaultMessageText: string;
  isBlocking: boolean;
}

export interface EzRuleBindingCatalog extends EzConfigurationCatalogEnvelope {
  entries: EzRuleBindingCatalogEntry[];
}

export interface EzRuleBindingCatalogEntry {
  subjectCode: string;
  ruleCode: string;
  bindingTypeCode: string;
  expressionJson?: string | null;
  isEnabled: boolean;
}

export interface EzTerminologyHealthReport extends EzConfigurationCatalogEnvelope {
  summary: EzTerminologyHealthSummary;
  diagnostics: EzTerminologyHealthDiagnostic[];
  evaluatedTokenCodes?: string[];
  usageEvidence?: EzTerminologyUsageHealthEvidence | null;
  freshness?: EzTerminologyFreshness | null;
}

export interface EzTerminologyFreshness {
  revisionToken: string;
  resolvedAtUtc: string;
  navigationRevalidateAfterSeconds: number;
  activeSessionRefreshAfterSeconds: number;
}

export interface EzTerminologyHealthSummary {
  candidateCount: number;
  tokenCount: number;
  resolvedTokenCount: number;
  errorCount: number;
  warningCount: number;
  isHealthy: boolean;
}

export interface EzTerminologyHealthDiagnostic {
  severityCode: string;
  reasonCode: string;
  tokenCode: string;
  relatedTokenCode?: string | null;
  dependencyPath?: string[] | null;
  matchedEntryCount: number;
}

export interface EzTerminologyUsageHealthEvidence {
  statusCode: "available" | "unavailable";
  releaseIdentifier?: string | null;
  generatedAtUtc?: string | null;
  declaredTokenCount: number;
  unusedCandidateCount: number;
  entries: EzTerminologyUsageHealthEntry[];
}

export interface EzTerminologyUsageHealthEntry {
  tokenCode: string;
  statusCode: "declared-used" | "not-declared-in-release";
  declaredUsageCount: number;
  clientUsageCount: number;
  serverUsageCount: number;
}

export function toDiagnosticSuccess<T>(data: T): PlatformConfigurationDiagnosticResult<T> {
  return {
    state: "resolved",
    data,
    error: null,
  };
}

export function toDiagnosticFailure<T>(error: unknown): PlatformConfigurationDiagnosticResult<T> {
  const displayError = mapPlatformConfigurationDiagnosticError(error);

  return {
    state: toDiagnosticState(displayError.statusCode),
    data: null,
    error: displayError,
  };
}

export function mapPlatformConfigurationDiagnosticError(
  error: unknown,
): PlatformConfigurationDiagnosticDisplayError {
  if (error instanceof HttpErrorResponse) {
    const problem = toProblemDetails(error.error);
    const statusCode = error.status || problem.status;
    const messages = new Set<string>();

    flattenValidationMessages(problem.errors).forEach((message) => messages.add(message));

    if (statusCode !== undefined && statusCode < 500 && normalizeText(problem.detail).length > 0) {
      messages.add(normalizeText(problem.detail));
    }

    if (messages.size === 0) {
      messages.add(getDefaultErrorMessage(statusCode));
    }

    return {
      title: statusCode !== undefined && statusCode >= 500
        ? getDefaultErrorTitle(statusCode)
        : normalizeText(problem.title) || getDefaultErrorTitle(statusCode),
      messages: [...messages],
      statusCode,
      reasonCode: normalizeText(problem.reasonCode) || undefined,
      matchedBindingCount: typeof problem.matchedBindingCount === "number"
        ? problem.matchedBindingCount
        : undefined,
      correlationId: error.headers?.get("X-Correlation-ID"),
    };
  }

  return {
    title: "Unexpected error",
    messages: ["Configuration diagnostics could not be loaded. Try again or contact support if the problem persists."],
  };
}

function toDiagnosticState(statusCode?: number): PlatformConfigurationDiagnosticState {
  switch (statusCode) {
    case 400:
      return "invalid";
    case 404:
      return "missing";
    case 409:
      return "conflict";
    default:
      return "unavailable";
  }
}

function toProblemDetails(errorBody: unknown): PlatformConfigurationProblemDetails {
  if (!errorBody || typeof errorBody !== "object" || Array.isArray(errorBody)) {
    return {};
  }

  return errorBody as PlatformConfigurationProblemDetails;
}

function flattenValidationMessages(errors: PlatformConfigurationProblemDetails["errors"]): string[] {
  if (!errors || typeof errors !== "object") {
    return [];
  }

  return Object.values(errors).flatMap((entry) => {
    if (Array.isArray(entry)) {
      return entry
        .map((item) => normalizeText(item))
        .filter((item) => item.length > 0);
    }

    const normalized = normalizeText(entry);
    return normalized.length > 0 ? [normalized] : [];
  });
}

function getDefaultErrorTitle(statusCode?: number): string {
  switch (statusCode) {
    case 400:
      return "Request could not be completed";
    case 404:
      return "Configuration data not found";
    case 409:
      return "Configuration conflict detected";
    default:
      return "Configuration diagnostics unavailable";
  }
}

function getDefaultErrorMessage(statusCode?: number): string {
  switch (statusCode) {
    case 400:
      return "Review the configuration, scope, and date values, then try again.";
    case 404:
      return "No matching configuration data was found for the supplied scope and date.";
    case 409:
      return "Multiple configuration records matched the supplied scope and date.";
    default:
      return "Configuration diagnostics could not be loaded. Try again or contact support if the problem persists.";
  }
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
